import { CSSProperties, useCallback, useRef } from 'react'
import { Typography, styled } from '@mui/material'
import LinearProgress, {
  linearProgressClasses,
} from '@mui/material/LinearProgress'
import { RawAudioMessage } from '@weblite/ts-action/message'
import { playAudio, pauseAudio, useAudioState } from '@weblite/audio'
import * as audioPlayerService from '$logic/audio-player'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { ActionHandler, ActionHandlerState } from '$common/action-handler'
import { useSeek, useContentFileStatus } from '$common/react-hooks/message'
import { getFileTitle, formatFileSize } from '$func/file'
import { formatDuration } from '$func/time'
import { MessageMenuIcon } from '../../components/menu-icon'

const StyledLinearProgress = styled(LinearProgress)(({ theme, value }) => ({
  width: '100%',
  height: 4,
  borderRadius: 2,
  margin: '10px 0',
  /* @noflip */
  direction: 'ltr', // in every lang

  [`&.${linearProgressClasses.colorPrimary}`]: {
    backgroundColor: theme.palette.neutral300,
    "&[data-from-me='true']": {
      backgroundColor: 'rgba(8, 13, 17, 0.15)',
    },
  },

  [`& .${linearProgressClasses.bar}`]: {
    borderRadius: 2,
    backgroundColor: theme.palette.secondary500,
    transform: `translateX(-${100 - (value || 0)}%) !important`,
    transition: 'none',
  },

  "&[data-from-me='true']": {
    [`& .${linearProgressClasses.bar}`]: {
      backgroundColor: theme.palette.neutral000,
    },
  },
}))

const Container = styled('div')`
  display: flex;
  position: relative;
  align-items: center;

  padding: 10px 10px 10px 30px;
  box-sizing: border-box;
  overflow: hidden;
`

const ContentContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const AudioMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.neutral500,
  lineHeight: '12px',
  "&[data-from-me='true']": {
    color: theme.palette.neutral200,
  },
}))

interface SeekThumbProps {
  value: number
}

const SeekThumb = styled('div')<SeekThumbProps>(({ theme, value }) => ({
  position: 'absolute',
  top: -4, // to be in the middle of progress bar
  /* @noflip */
  left: `clamp(0px, calc(${value}% - 6px), calc(100% - 12px))`,

  backgroundColor: theme.palette.secondary500,
  "&[data-from-me='true']": {
    backgroundColor: theme.palette.neutral000,
  },
  width: 12,
  height: 12,
  borderRadius: '50%',

  boxShadow: '0px 1px 4px rgba(0, 0, 0, 0.1)',
  cursor: 'pointer',
}))

const playStateToIconMap = {
  NOT_PLAYING: PlayIcon,
  PLAYING: PauseIcon,
  PAUSED: PlayIcon,
}

export function Audio({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  iid,
  roomId,
  fromMe,
}: {
  attachment: RawAudioMessage['attachment']
  statusComp: JSX.Element | null
  uploadId: string
  rootStyle?: CSSProperties
  iid: number
  roomId: string
  fromMe: boolean
}) {
  const { duration, fileId, filename, cover, size } = attachment

  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId, messageType: 'AUDIO' })

  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const canSeek = state === 'AVAILABLE'

  const seekValue = useSeek({
    src: blobURL,
    canSeek,
    containerRef,
    seekContainerRef,
  })

  const downloadingOrUploading =
    state === 'UPLOADING' || state === 'DOWNLOADING'

  const { sizeText: fileSizeText, unit: fileSizeUnit } = formatFileSize(size)
  const { sizeText: progressedSizeText } = formatFileSize(
    Math.round(size * (percentage / 100)),
    fileSizeUnit,
    false,
  )

  // TODO: Use HostUrl
  const coverUrl = cover

  const audioTitle = getFileTitle(filename)
  const audioDurationText = formatDuration(duration)

  // in case of playing
  const spentDurationText = formatDuration(duration * seekValue)

  const CustomActionIcon =
    state === 'AVAILABLE' ? playStateToIconMap[playState] : undefined

  const togglePlay = useCallback(() => {
    if (playState === 'PLAYING') pauseAudio(blobURL)
    else {
      audioPlayerService.setData(roomId, iid)
      playAudio(blobURL, duration / 1000, { name: audioTitle, type: 'AUDIO' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playState, blobURL, duration, audioTitle])

  const seekPercentage = Math.round(seekValue * 100)

  return (
    <>
      <Container ref={containerRef} style={{ width: '280px', ...rootStyle }}>
        <ActionHandler
          state={state as ActionHandlerState}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={startDownload}
          onCustomActionIconClick={togglePlay}
          customActionIconComp={CustomActionIcon && <CustomActionIcon />}
          coverUrl={coverUrl}
          fromMe={fromMe}
        />

        <ContentContainer>
          <Typography
            color={fromMe ? 'neutral000' : 'neutral900'}
            variant="caption1"
            sx={{ display: 'block', lineHeight: '12px' }}
            noWrap
          >
            {audioTitle}
          </Typography>

          <div
            ref={seekContainerRef}
            style={{
              width: '100%',
              position: 'relative',
              cursor: canSeek ? 'pointer' : 'default',
            }}
          >
            <StyledLinearProgress
              variant="determinate"
              value={seekPercentage}
              data-from-me={fromMe}
            />

            <SeekThumb
              value={seekPercentage}
              style={{
                display: canSeek ? 'unset' : 'none',
              }}
              data-from-me={fromMe}
            />
          </div>

          <div
            style={{
              display: 'flex',
              overflow: 'hidden',
              alignItems: 'center',
            }}
          >
            {!downloadingOrUploading && (
              <>
                <AudioMetaTypography data-from-me={fromMe}>
                  {audioDurationText}
                </AudioMetaTypography>
                {playState !== 'NOT_PLAYING' && (
                  <AudioMetaTypography
                    data-from-me={fromMe}
                    sx={{
                      color: '#c4c4c4',
                      margin: '0 4px',
                    }}
                  >
                    /
                  </AudioMetaTypography>
                )}
              </>
            )}

            {(playState === 'PLAYING' || playState === 'PAUSED') && (
              <AudioMetaTypography data-from-me={fromMe}>
                {spentDurationText}
              </AudioMetaTypography>
            )}
            {(!downloadingOrUploading ||
              playState === 'PLAYING' ||
              playState === 'PAUSED') &&
              state !== 'AVAILABLE' && (
                <AudioMetaTypography
                  data-from-me={fromMe}
                  sx={{ marginX: '4px' }}
                >
                  /
                </AudioMetaTypography>
              )}

            {state !== 'AVAILABLE' && (
              <AudioMetaTypography
                data-from-me={fromMe}
                noWrap
                sx={{
                  color: downloadingOrUploading ? 'primary.main' : 'gray',
                }}
              >
                {downloadingOrUploading
                  ? `${progressedSizeText} از ${fileSizeText}`
                  : fileSizeText}
              </AudioMetaTypography>
            )}
          </div>
        </ContentContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} fromMe={fromMe} />
      </Container>
      {statusComp}
    </>
  )
}

Audio.defaultProps = {
  rootStyle: null,
}
